/* eslint-disable jsdoc/no-undefined-types */
import getPath from '@neonaut/lib-js/es/object/getPath';
import {observeState} from '@neonaut/lib-redux/es/observe-state';

import {BaseController} from '@mapsight/core/lib/base/controller';
import {baseReducer} from '@mapsight/core/lib/base/reducer';

import {RATINGS_GET_RESULT, RATINGS_SEND, RATINGS_SEND_RESULT} from './actions';


export const SendingState = Object.freeze({
	SENDING: 'SENDING',
	SENT: 'SENT',
	FAILED: 'FAILED',
	WRONG_CAPTCHA: 'WRONG_CAPTCHA',
});


// Store
// die Action (Rating-Daten, isEdit), die die Bewertung versendet, legt diese zugleich im Store ab
// wenn der Server das erfolgreiche Speichern quittiert, wird das im Store vermerkt
// → drei Zustände: neue Bewertung versendet, Aktualisierung gesendet, Zustand gespeichert, ermöglicht unterschiedliche Beschriftung des Absenden-Knopfes
// WONT: eine Zustand für die Formulardaten je POI, so dass man zwischen POI wechseln kann und dann weiter arbeiten kann


// durch folgendes Layout kann beim state-Update bei der Server-Antowrt das rating als Marker erneut mitgeschickt werden.
// der state wird nur aktualisiert, wenn das rating-Objekt dasselbe ist wie beim Absenden an den Server
// der tree unterhalb von storage wird auch im Browser gespeichert
// [RATINGS]
// |- storage
// |  |– features
// |  |  |- <featureSourceId> ...
// |  |  |   |– <poi-id> ...
// |  |  |   |   |- ratingData: Rating-Daten
// |  |  |   |   |- state

// |  |- token
// |  |- author

// |- captcha
// |- sendRequestIds
// |  |- <featureSourceId> ...
// |  |   |- <poiId> ...

/** @typedef {{
		captcha: {
			error: boolean | undefined,
			question: string | undefined
		},
		sendRequestIds: Record<string, Record<string, number>>,
		storage: {
			author: string | undefined,
			token: string | undefined,
			features: Record<string, Record<string, {
				state: undefined | null | 'SENDING' | 'SENT' | 'FAILED' | WRONG_CAPTCHA,
				ratingData: {
					rating,
					lang,
					comment,
				}
			}>>
		},
 * }} RatingsState
 */


export const RATINGS = 'RATINGS';
const STORAGE_KEY = 'given-ratings';

function loadFromStorage() {
	try {
		const serializedState = localStorage.getItem(STORAGE_KEY);
		if (!serializedState) return undefined;
		return JSON.parse(serializedState);
	} catch (e) {
		return undefined;
	}
}

async function saveState(state) {
	try {
		const serializedState = JSON.stringify(state);
		localStorage.setItem(STORAGE_KEY, serializedState);
	} catch (e) {
		// Ignore
	}
}

export function initialState() {
	const stored = loadFromStorage();

	// SendingState.SENDING auf .FAILED umschreiben, da wir das Ergebnis ja nicht kennen und die Seite jetzt neu geladen wird
	// ebenso WRONG_CAPTCHA weil nach dem Reload das falsche Captcha eh nicht mehr da steht
	// das hier is ja noch nicht im Store, daher ist stored noch mutable
	const features = stored?.features || {};
	Object.keys(features).forEach(sourceId => {
		const pois = features[sourceId] || {};
		Object.keys(pois).forEach(poiId => {
			if (pois[poiId].state === SendingState.SENDING || pois[poiId].state === SendingState.WRONG_CAPTCHA) {
				pois[poiId].state = SendingState.FAILED;
			}
		});
	});

	return Object.assign(
		{
			captcha: {
				error: null,
				question: null,
			},
			storage: {
				author: null,
				token: null,
				features: {}
			}
		},
		stored ? {storage: stored} : undefined
	);
}

/**
 * @param {RatingsState} state
 * @param {any} action
 * @returns {RatingsState}
 */
function ratingsGetResultReducer(state, action) {
	const storage = state.storage || {};
	const features = storage.features || {};

	const {author, id, token: t, ...ratingData} = action.result || {}; // token?
	const token = t || storage.token;


	const oldData = getPath(state, ['storage', 'features', action.sourceId, action.poiId]);
	const oldDate = new Date(oldData?.ratingData?.updateDate);
	const newDate = new Date(ratingData?.ratingData?.updateDate);

	console.log('ratingsGetResultReducer', {
		state,
		action,
		oldData,
		oldDate,
		newDate,
		cond: !oldData || oldDate < newDate
	});


	if (!oldData
		|| (
			(!oldData.state || oldData.state === SendingState.SENT)
			&& oldDate <= newDate
		)
	) {
		return {
			...state,
			storage: {
				...storage,
				// author, // author ignorieren. das schützt uns davor, falls ein get ankommt, während ein edit mit einem geänderten Namen unterwegs ist
				token, // falls es mitgesendet wurde. Das ist derzeit nicht der Fall, eine zusätzliche Identifizierung via Cookie fehlt im Backend
				features: {
					...features,
					[action.sourceId]: {
						...features[action.sourceId],
						[action.poiId]: {
							...(features[action.sourceId] || {})[action.poiId],
							ratingData
						}
					}
				}
			}
		};
	}

	return state;
}

/**
 * @param {RatingsState} state
 * @param {any} action
 * @returns {RatingsState}
 */
// eigentlich bräuchte man für das author-Update eine eigene globale requestId, falls während das eine send unterwegs ist eine anderer POI bearbeitet wird und dort auch noch der Autorname korrigiert wird
// das ist aber so ein sonderfall, in den investiert man keine Zeit. der User kann das durch erneutes Ändern und Senden ja wieder korrigieren
function ratingsSendReducer(state, {type, sendRequestId, sourceId, poiId, author, token, ...ratingData}) {
	const lastRequestId = getPath(state, ['sendRequestIds', sourceId, poiId], null);
	const oldSendRequestIds = (state.sendRequestIds || {});
	const features = (state.storage?.features || {});

	console.warn('ratingsSendReducer', {
		type,
		lastRequestId,
		sendRequestId,
		sourceId,
		poiId,
		author,
		token,
		ratingData,
		SENDING: SendingState.SENDING,
		cond: lastRequestId === null || lastRequestId === sendRequestId
	});

	if (lastRequestId === null || sendRequestId > lastRequestId) {
		const result = {
			...state,
			sendRequestIds: {
				...oldSendRequestIds,
				[sourceId]: {
					...oldSendRequestIds[sourceId],
					[poiId]: sendRequestId
				}
			},
			storage: {
				...state.storage,
				author,
				features: {
					...features,
					[sourceId]: {
						...features[sourceId],
						[poiId]: {
							state: SendingState.SENDING,
							ratingData
						}
					}
				}
			}
		};
		console.log('+ratingsSendReducer returns', result);
		return result;
	}

	return state;
}

/**
 * @param state
 * @param sendRequestId
 * @param sourceId
 * @param poiId
 * @param ratingData
 * @return {RatingsState}
 */
function ratingsSendResultReducer(state, {sendRequestId, sourceId, poiId, state: operationState, token}) {
	const lastRequestId = getPath(state, ['sendRequestIds', sourceId, poiId], null);
	const features = (state.storage?.features || {});
	const newToken = operationState === SendingState.SENT && token ? token : state.token;

	console.log('ratingsSendResultReducer', {
		sendRequestId,
		sourceId,
		poiId,
		operationState,
		token,
		lastRequestId,
		features,
		newToken,
		state
	});

	if (lastRequestId !== null && lastRequestId === sendRequestId) {
		return {
			...state,
			storage: {
				...state.storage,
				token: newToken,
				features: {
					...features,
					[sourceId]: {
						...features[sourceId],
						[poiId]: {
							...(features[sourceId] || {})[poiId],
							state: operationState
						}
					}
				}
			}
		};
	} else if (token) {
		// FALLBACK: token immer übernehmen. Token ohne weitere Daten kommt aktuell aber nicht vor.
		return {
			...state,
			token
		};
	}

	return state;
}


export class RatingsController extends BaseController {
	init() {
		observeState(this.getStore(), _ => (_[RATINGS] || {})['storage'], saveState);
	}

	/**
	 * @param {undefined | RatingState} state
	 * @returns {RatingState}
	 */
	reduce(
		state = initialState(),
		action,
		_globalState,
	) {
		switch (action.type) {
			case RATINGS_GET_RESULT:
				return ratingsGetResultReducer(state, action);
			case RATINGS_SEND:
				return ratingsSendReducer(state, action);
			case RATINGS_SEND_RESULT:
				return ratingsSendResultReducer(state, action);
		}
		return baseReducer(state, action);
	}
}
